import React, { useState } from 'react';
import { createPackage } from '../../utils/financialAnalystApi';
import './FinancialAnalyst.css';

const TEMPLATES = [
  {
    key: 'monthly_operating',
    name: 'Monthly Operating Report',
    desc: 'Full P&L critique with department commentary and KPIs',
    icon: '📊',
  },
  {
    key: 'quarterly_portfolio',
    name: 'Portfolio Quarterly Review',
    desc: 'Consolidated multi-property quarterly analysis',
    icon: '🏢',
  },
  {
    key: 'variance',
    name: 'Variance Report',
    desc: 'Budget-to-actual variance analysis with waterfall',
    icon: '📉',
  },
  {
    key: 'lender_compliance',
    name: 'Lender Compliance',
    desc: 'DSCR, NOI waterfall, and covenant compliance',
    icon: '🏦',
  },
  {
    key: 'investor_letter',
    name: 'Investor Letter',
    desc: 'Quarterly investor letter with performance narrative',
    icon: '✉️',
  },
];

const PackageBuilder = ({ locations, currentOrgId, onPackageCreated }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [locationId, setLocationId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    if (!selectedTemplate || !locationId || !periodStart || !periodEnd) {
      setError('Please fill in all required fields.');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const template = TEMPLATES.find(t => t.key === selectedTemplate);
      const periodType = selectedTemplate === 'quarterly_portfolio' || selectedTemplate === 'investor_letter'
        ? 'quarterly' : 'monthly';

      const result = await createPackage({
        locationId: parseInt(locationId),
        orgId: currentOrgId,
        templateType: selectedTemplate,
        periodType,
        periodStart,
        periodEnd,
        title: title || `${template.name} — ${new Date(periodStart).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
      });

      if (onPackageCreated) onPackageCreated(result.package || result);
    } catch (err) {
      console.error('Failed to create package:', err);
      setError('Failed to create package. Please try again.');
    }
    setCreating(false);
  };

  // Auto-fill period end when start changes (month-end)
  const handlePeriodStartChange = (value) => {
    setPeriodStart(value);
    if (value) {
      const date = new Date(value);
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      setPeriodEnd(lastDay.toISOString().split('T')[0]);
    }
  };

  return (
    <div className="fa-package-builder">
      <h3>Create Financial Package</h3>

      {/* Template Selection */}
      <div className="fa-form-group">
        <div className="fa-form-label">Select Template</div>
        <div className="fa-template-grid">
          {TEMPLATES.map((t) => (
            <div
              key={t.key}
              className={`fa-template-card ${selectedTemplate === t.key ? 'selected' : ''}`}
              onClick={() => setSelectedTemplate(t.key)}
            >
              <div style={{ fontSize: 24, marginBottom: 6 }}>{t.icon}</div>
              <div className="fa-template-card-title">{t.name}</div>
              <div className="fa-template-card-desc">{t.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Property */}
      <div className="fa-form-group">
        <label className="fa-form-label">Property</label>
        <select
          className="fa-form-select"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        >
          <option value="">Select a property...</option>
          {(locations || []).map((loc) => (
            <option key={loc.id} value={loc.id}>{loc.name}</option>
          ))}
        </select>
      </div>

      {/* Period */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="fa-form-group">
          <label className="fa-form-label">Period Start</label>
          <input
            type="date"
            className="fa-form-input"
            value={periodStart}
            onChange={(e) => handlePeriodStartChange(e.target.value)}
          />
        </div>
        <div className="fa-form-group">
          <label className="fa-form-label">Period End</label>
          <input
            type="date"
            className="fa-form-input"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </div>
      </div>

      {/* Title */}
      <div className="fa-form-group">
        <label className="fa-form-label">Title (optional)</label>
        <input
          type="text"
          className="fa-form-input"
          placeholder="Auto-generated if left blank"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      {error && <div style={{ color: '#EF4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <button
        className="fa-btn fa-btn-primary"
        onClick={handleCreate}
        disabled={creating || !selectedTemplate || !locationId || !periodStart || !periodEnd}
        style={{ width: '100%', justifyContent: 'center', padding: '10px 16px' }}
      >
        {creating ? (
          <>
            <div className="fa-progress-spinner" style={{ width: 14, height: 14, borderWidth: 2, marginBottom: 0 }} />
            Creating...
          </>
        ) : (
          'Create Package'
        )}
      </button>
    </div>
  );
};

export default PackageBuilder;
